import {
  Component,
  ViewChild,
  AfterViewInit,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  Inject,
  OnInit,
  OnDestroy,
} from '@angular/core';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  DropDownOptions,
  ExtendedKeycloakUser,
  IItemToRequest,
  MyPurchaseForm,
  StoreDropdowns,
  UserInformation,
} from 'src/app/shared/models/app-models';
import { PermissionsService } from 'src/app/core/services/permissions/permission.service';
import { AttachmentTypeIdEnum } from 'src/app/shared/models/database-models';
import _ from 'lodash';
import { DialogComponent } from 'src/app/shared/components/dialog/dialog.component';
import {
  RfqOutput,
  RfqOutputData,
  RfqOutputService,
} from '../../rfq-output/rfq-output.service';
import { Subject, takeUntil } from 'rxjs';
import {
  SelectVendorTableSelectionMode,
  Vendor,
} from 'src/app/shared/components/select-vendor-table/select-vendor-table.component';

export enum AttachmentTaggingType {
  DOCUMENT_TO_VENDOR = 'DOCUMENT_TO_VENDOR',
  QUOTATION = 'QUOTATION',
}

export interface AttachmentTaggingData {
  type: AttachmentTaggingType;
  MPDocumentNumber: string;
  filename: string;
  selectedPurpose: string;
  initialSelectedItemNumbers?: number[];
  initialSelectedVendorNumbers?: string[];
  initialSelectedRFQNumber?: string;
}

export interface AttachmentTaggingFormData {
  selectedItemNumbers: number[];
  selectedVendorNumbers: string[];
  selectedRFQNumber: string;
}

export interface AttachmentTaggingActionResult {
  data: AttachmentTaggingData;
  form: AttachmentTaggingFormData;
  isSubmit:boolean
}

export type AttachmentTaggingResult = undefined | AttachmentTaggingActionResult;

@Component({
  selector: 'app-attachment-tagging',
  templateUrl: './attachment-tagging.component.html',
  styleUrls: ['./attachment-tagging.component.scss'],
  providers: [],
})
export class AttachmentTaggingComponent implements OnDestroy {
  form!: MyPurchaseForm;
  destroy$: Subject<boolean> = new Subject<boolean>();
  rfqNumberDropdownOptions: DropDownOptions[] = [];
  filterVendorNumbers: Vendor['vendorNumber'][] = [];
  userList: ExtendedKeycloakUser[] = [];


  public formData: AttachmentTaggingFormData = {
    selectedItemNumbers: [],
    selectedVendorNumbers: [],
    selectedRFQNumber: '',
  };

  constructor(
    public dialogRef: MatDialogRef<AttachmentTaggingComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AttachmentTaggingData,
    private store: Store<{
      form: MyPurchaseForm;
      dp: StoreDropdowns;
      user: UserInformation;
    }>,
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private permissionsService: PermissionsService,
    public rfqOutputService: RfqOutputService
  ) {
    console.log('THIS DATA', this.data);
    this.store
      .select('dp')
      .pipe(takeUntil(this.destroy$))
      .subscribe((dp: StoreDropdowns) => {
        this.userList = dp.UserList as ExtendedKeycloakUser[];
      });
    if (this.data.initialSelectedItemNumbers) {
      this.formData.selectedItemNumbers = _.cloneDeep(
        this.data.initialSelectedItemNumbers
      );
    }
    if (this.data.initialSelectedVendorNumbers) {
      this.formData.selectedVendorNumbers = _.cloneDeep(
        this.data.initialSelectedVendorNumbers
      );
    }
    if (this.data.initialSelectedRFQNumber) {
      this.formData.selectedRFQNumber = this.data.initialSelectedRFQNumber;
    }

    this.rfqOutputService.rfqOutput$
      .pipe(takeUntil(this.destroy$))
      .subscribe((rfqOutput: RfqOutput) => {
        if (
          rfqOutput.data?.RFQ?.[0]?.MPDocumentNumber ===
          this.data.MPDocumentNumber
        ) {
          this.rfqNumberDropdownOptions = this.mapRfqOptions(
            rfqOutput.data.RFQ
          );
          this.filterVendorNumbers = this.getFilterVendorNumbers(
            rfqOutput.data.RFQ,
            this.formData.selectedRFQNumber
          );
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  onSubmit() {
    const dialogResult: AttachmentTaggingActionResult = {
      data: this.data,
      form: this.formData,
      isSubmit:true
    };

    this.dialogRef.close(dialogResult);
  }

  onCancel() {
    const dialogResult: AttachmentTaggingActionResult = {
      data: this.data,
      form: this.formData,
      isSubmit:false
    };

    this.dialogRef.close(dialogResult);
  }

  public onItemNumberSelectedChange(event: number[]) {
    this.formData.selectedItemNumbers = event;
  }

  public onVendorNumberSelectedChange(event: string[]) {
    this.formData.selectedVendorNumbers = event;
  }

  public onRFQNumberChange(event: any) {
    // Reset selected vendor numbers if RFQNumber changed
    this.formData.selectedVendorNumbers = [];

    const rfqOutputData = this.rfqOutputService.rfqOutputData;
    if (
      rfqOutputData?.RFQ?.[0]?.MPDocumentNumber === this.data.MPDocumentNumber
    ) {
      this.filterVendorNumbers = this.getFilterVendorNumbers(
        rfqOutputData.RFQ,
        this.formData.selectedRFQNumber
      );
    }
  }

  getuserName(id: string): string {
    const user = this.userList.find((user) => user.id === id);
    return user?.last_name ?? '';
  }

  public mapRfqOptions(rfqOutput?: RfqOutputData['RFQ']) {
    if (!rfqOutput) {
      return [];
    }

    const uniqueRFQNumbers: Set<string> = new Set();
    for (const rfqItem of rfqOutput) {
      if (rfqItem.RFQNumber && rfqItem.IsActive) {
        uniqueRFQNumbers.add(rfqItem.RFQNumber);
      }
    }

    const options = _.sortBy(Array.from(uniqueRFQNumbers)).map((rfqNumber) => {
      const rfqItem = rfqOutput.find(item => item.RFQNumber === rfqNumber);

      const value = rfqItem && rfqItem.PE ? `${rfqNumber} - ${this.getuserName(rfqItem.PE)}` : rfqNumber;

      return {
        id: rfqNumber,
        value: value,
      };
    });

    return options;

  }

  public getFilterVendorNumbers(
    rfqOutput: RfqOutputData['RFQ'],
    selectedRfqNumber: string
  ) {
    if (!selectedRfqNumber) {
      return [];
    }

    return rfqOutput
      .filter(
        (rfqItem) =>
          rfqItem.RFQNumber === selectedRfqNumber &&
          rfqItem.IsActive &&
          rfqItem.VendorNumber
      )
      .map((rfqItem) => rfqItem.VendorNumber!);
  }

  public get AttachmentTaggingType(): typeof AttachmentTaggingType {
    return AttachmentTaggingType;
  }
  public get SelectVendorTableSelectionMode(): typeof SelectVendorTableSelectionMode {
    return SelectVendorTableSelectionMode;
  }
}
